import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaBullseye, FaHandsHelping, FaLeaf, FaUserFriends } from 'react-icons/fa';
import { advisoryBoardService, blogService, campaignService, eventService, homeSettingService, projectService } from '../services';
import { resolveMediaUrl } from '../services/api';
import About from '../components/sections/About';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import BgImg1 from '../components/assets/yapImg/bg-01.jpeg';
import BgImg2 from '../components/assets/yapImg/bg-02.jpeg';
import 'swiper/css';
import 'swiper/css/pagination';

const heroSlides = [
    {
        image: BgImg1,
        title: 'Turning youth potential into measurable community impact.',
        subtitle: 'We build opportunities through education, livelihood programs, and social action led by young changemakers.',
    },
    {
        image: BgImg2,
        title: 'Empowering communities with skill, purpose, and leadership.',
        subtitle: 'From local training to national initiatives, YAP supports youth to create lasting social change.',
    },
];

const Home = () => {
    const [projects, setProjects] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [news, setNews] = useState([]);
    const [events, setEvents] = useState([]);
    const [advisors, setAdvisors] = useState([]);
    const [homeSetting, setHomeSetting] = useState(null);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [projectsRes, campaignsRes, blogsRes, eventsRes, advisorsRes] = await Promise.all([
                    projectService.getAll({ per_page: 3 }),
                    campaignService.getAll({ per_page: 3 }),
                    blogService.getAll({ per_page: 3 }),
                    eventService.getAll({ per_page: 3 }),
                    advisoryBoardService.getAll({ per_page: 3 }),
                ]);

                setProjects(projectsRes.data?.data || []);
                setCampaigns(campaignsRes.data?.data || []);
                setNews(blogsRes.data?.data || []);
                setEvents(eventsRes.data?.data || []);
                setAdvisors(advisorsRes.data?.data || []);

                const homeSettingRes = await homeSettingService.get();
                setHomeSetting(homeSettingRes.data || null);
            } catch (error) {
                console.error('Error loading homepage content:', error);
            }
        };

        loadData();
    }, []);

    return (
        <>
            <Helmet>
                <title>Home - YAP NGO</title>
                <meta name="description" content="Youth Association of Pakistan: empowering youth through education, projects, and community development." />
            </Helmet>

            <section className="relative overflow-hidden text-white">
                <Swiper
                    modules={[Autoplay, Pagination]}
                    loop
                    autoplay={{ delay: 5000, disableOnInteraction: false }}
                    pagination={{ clickable: true }}
                    className="home-hero-swiper"
                >
                    {heroSlides.map((slide, index) => (
                        <SwiperSlide key={index}>
                            <div
                                className="relative min-h-[620px] bg-cover bg-center py-20"
                                style={{ backgroundImage: `url(${slide.image})` }}
                            >
                                <div className="absolute inset-0 bg-[linear-gradient(110deg,rgba(2,44,34,0.88),rgba(15,23,42,0.72))]"></div>
                                <div className="relative mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-2 lg:px-8">
                                    <div>
                                        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-emerald-200">Youth Association of Pakistan</p>
                                        <h1 className="mt-4 text-5xl font-black leading-tight md:text-6xl">{slide.title}</h1>
                                        <p className="mt-5 max-w-xl text-lg text-emerald-50">{slide.subtitle}</p>
                                        <div className="mt-8 flex flex-wrap gap-3">
                                            <Link to="/donate" className="rounded-full bg-white px-6 py-3 font-bold text-emerald-700 transition hover:bg-emerald-100">Support a Campaign</Link>
                                            <Link to="/volunteer" className="rounded-full border border-white/60 px-6 py-3 font-bold text-white transition hover:bg-white/10">Become a Volunteer</Link>
                                        </div>
                                    </div>
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-md"><p className="text-4xl font-black">50K+</p><p className="text-emerald-100">Youth Beneficiaries</p></div>
                                        <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-md"><p className="text-4xl font-black">25+</p><p className="text-emerald-100">Active Projects</p></div>
                                        <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-md"><p className="text-4xl font-black">5</p><p className="text-emerald-100">Provinces Covered</p></div>
                                        <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-md"><p className="text-4xl font-black">95%</p><p className="text-emerald-100">Program Completion</p></div>
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </section>

            <section className="bg-slate-50 py-14">
                <div className="mx-auto grid max-w-7xl gap-5 px-4 md:grid-cols-3 lg:px-8">
                    <div className="rounded-2xl border border-emerald-100 bg-white p-6 shadow-sm"><FaBullseye className="text-2xl text-emerald-600" /><h3 className="mt-4 text-xl font-bold text-slate-800">Mission Focused</h3><p className="mt-2 text-slate-600">Every initiative is designed around long-term youth empowerment outcomes.</p></div>
                    <div className="rounded-2xl border border-cyan-100 bg-white p-6 shadow-sm"><FaUserFriends className="text-2xl text-cyan-600" /><h3 className="mt-4 text-xl font-bold text-slate-800">Community Driven</h3><p className="mt-2 text-slate-600">Programs are co-created with local communities and field volunteers.</p></div>
                    <div className="rounded-2xl border border-amber-100 bg-white p-6 shadow-sm"><FaHandsHelping className="text-2xl text-amber-600" /><h3 className="mt-4 text-xl font-bold text-slate-800">Transparent Impact</h3><p className="mt-2 text-slate-600">We share progress openly and track impact with clear indicators.</p></div>
                </div>
            </section>

            <About />

            <section className="bg-slate-900 py-16 text-white">
                <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 lg:grid-cols-2 lg:px-8">
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-300">Featured Video</p>
                        <h2 className="mt-2 text-3xl font-black">Inside YAP Programs</h2>
                        <p className="mt-3 text-slate-300">
                            See how youth-led initiatives transform lives through education, mentoring, and practical support.
                        </p>
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-white/10 bg-black shadow-xl">
                        {homeSetting?.video_path ? (
                            <video
                                src={resolveMediaUrl(homeSetting.video_path)}
                                autoPlay
                                muted
                                loop
                                playsInline
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <div className="flex h-72 items-center justify-center text-slate-400">Upload a home video from CMS - Home Settings</div>
                        )}
                    </div>
                </div>
            </section>

            <section className="bg-white py-16">
                <div className="mx-auto max-w-7xl px-4 lg:px-8">
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-600">Impact Graphs</p>
                    <h2 className="mt-2 text-3xl font-black text-slate-900">Community Progress Snapshot</h2>
                    <div className="mt-8 grid gap-6 md:grid-cols-3">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                            <p className="text-sm font-semibold text-slate-600">Project Completion</p>
                            <div className="mt-3 h-3 rounded-full bg-slate-200">
                                <div className="h-3 rounded-full bg-emerald-500" style={{ width: `${Math.min(100, Math.round((projects.filter((p) => p.status === 'completed').length / Math.max(1, projects.length)) * 100))}%` }}></div>
                            </div>
                            <p className="mt-2 text-sm text-slate-700">{projects.filter((p) => p.status === 'completed').length} of {projects.length} projects completed</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                            <p className="text-sm font-semibold text-slate-600">Campaign Funding</p>
                            <div className="mt-3 h-3 rounded-full bg-slate-200">
                                <div
                                    className="h-3 rounded-full bg-cyan-500"
                                    style={{
                                        width: `${Math.min(
                                            100,
                                            Math.round(
                                                (campaigns.reduce((sum, c) => sum + Number(c.current_amount || 0), 0) /
                                                    Math.max(1, campaigns.reduce((sum, c) => sum + Number(c.goal_amount || 0), 0))) *
                                                    100
                                            )
                                        )}%`,
                                    }}
                                ></div>
                            </div>
                            <p className="mt-2 text-sm text-slate-700">
                                PKR {campaigns.reduce((sum, c) => sum + Number(c.current_amount || 0), 0).toLocaleString()} raised
                            </p>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                            <p className="text-sm font-semibold text-slate-600">Program Activity</p>
                            <div className="mt-4 flex h-24 items-end gap-2">
                                {[projects.length, events.length, news.length].map((count, idx) => (
                                    <div key={idx} className="flex-1 rounded-t bg-emerald-500/80" style={{ height: `${Math.max(20, count * 20)}%` }}></div>
                                ))}
                            </div>
                            <p className="mt-2 text-xs text-slate-600">Bars: Projects / Events / News</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-white py-16">
                <div className="mx-auto max-w-7xl px-4 lg:px-8">
                    <div className="mb-8 flex items-center justify-between">
                        <div><p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-600">Projects</p><h2 className="text-3xl font-black text-slate-900">Current Initiatives</h2></div>
                        <Link to="/projects" className="inline-flex items-center gap-2 text-sm font-bold text-emerald-700 hover:text-emerald-600">View all <FaArrowRight /></Link>
                    </div>
                    <div className="grid gap-6 md:grid-cols-3">
                        {projects.map((project) => (
                            <article key={project.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                                <img src={project.featured_image ? resolveMediaUrl(project.featured_image) : 'https://placehold.co/600x400?text=Project'} alt={project.title} className="h-48 w-full object-cover" />
                                <div className="p-5">
                                    <h3 className="text-lg font-bold text-slate-800">{project.title}</h3>
                                    <p className="mt-2 text-sm text-slate-600 line-clamp-3">{project.description}</p>
                                    <Link to={`/projects/${project.id}`} className="mt-4 inline-block text-sm font-semibold text-emerald-600 hover:text-emerald-500">Explore Project</Link>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-slate-900 py-16 text-white">
                <div className="mx-auto max-w-7xl px-4 lg:px-8">
                    <div className="mb-8 flex items-center justify-between">
                        <div><p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-300">Campaigns</p><h2 className="text-3xl font-black">Support Active Campaigns</h2></div>
                        <Link to="/donate" className="inline-flex items-center gap-2 text-sm font-bold text-emerald-300 hover:text-emerald-200">Donate now <FaArrowRight /></Link>
                    </div>
                    <div className="grid gap-6 md:grid-cols-3">
                        {campaigns.map((campaign) => (
                            <article key={campaign.id} className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                                <h3 className="text-xl font-bold">{campaign.title}</h3>
                                <p className="mt-2 text-sm text-slate-300 line-clamp-3">{campaign.description}</p>
                                <div className="mt-4 h-2 rounded-full bg-white/10"><div className="h-2 rounded-full bg-emerald-400" style={{ width: `${Math.min(100, Math.round((Number(campaign.current_amount || 0) / Math.max(1, Number(campaign.goal_amount || 1))) * 100))}%` }}></div></div>
                                <p className="mt-2 text-xs text-emerald-200">PKR {Number(campaign.current_amount || 0).toLocaleString()} of PKR {Number(campaign.goal_amount || 0).toLocaleString()}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-slate-50 py-16">
                <div className="mx-auto max-w-7xl px-4 lg:px-8">
                    <div className="mb-8 flex items-center justify-between">
                        <div><p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-600">Upcoming</p><h2 className="text-3xl font-black text-slate-900">Events</h2></div>
                        <Link to="/events" className="inline-flex items-center gap-2 text-sm font-bold text-emerald-700 hover:text-emerald-600">View Events <FaArrowRight /></Link>
                    </div>
                    <div className="grid gap-6 md:grid-cols-3">
                        {events.map((event) => (
                            <article key={event.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <h3 className="text-lg font-bold text-slate-800">{event.title}</h3>
                                <p className="mt-2 text-sm text-slate-600">{event.location}</p>
                                <p className="mt-1 text-sm font-semibold text-emerald-700">{event.event_date ? new Date(event.event_date).toLocaleDateString() : '-'}</p>
                                <p className="mt-3 text-sm text-slate-600 line-clamp-3">{event.description}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-slate-900 py-16">
                <div className="mx-auto max-w-7xl px-4 lg:px-8">
                    <div className="mb-8 flex items-center justify-between">
                        <div>
                            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-600">Leadership</p>
                            <h2 className="text-3xl font-black text-white pt-3">Advisory Board Highlights</h2>
                        </div>
                        <Link to="/advisory-board" className="inline-flex items-center gap-2 text-sm font-bold text-emerald-700 hover:text-emerald-600">Meet All <FaArrowRight /></Link>
                    </div>
                    <div className="grid text-white gap-6 md:grid-cols-3">
                        

                        {advisors.map((advisor) => (
                            <article key={advisor.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                <h3 className="text-lg font-bold text-slate-800">{advisor.name}</h3>
                                <p className="mt-2 text-sm font-semibold text-emerald-700">{advisor.position}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white py-16">
                <div className="mx-auto max-w-7xl px-4 lg:px-8">
                    <div className="mb-8 flex items-center justify-between">
                        <div><p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-600">Latest Updates</p><h2 className="text-3xl font-black text-slate-900">News & Insights</h2></div>
                        <Link to="/news" className="inline-flex items-center gap-2 text-sm font-bold text-emerald-700 hover:text-emerald-600">Read more <FaArrowRight /></Link>
                    </div>
                    <div className="grid gap-6 md:grid-cols-3">
                        {news.map((item) => (
                            <article key={item.id} className="rounded-2xl border border-slate-200 p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                                <div className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700"><FaLeaf className="mr-1 mt-[2px]" /> Update</div>
                                <span className="mt-3 text-lg font-bold text-slate-800">{item.image}</span>
                                <h3 className="mt-3 text-lg font-bold text-slate-800">{item.title}</h3>
                                <p className="mt-2 text-sm text-slate-600 line-clamp-3">{item.content}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

export default Home;
